// src/pages/administrasi/KasKeluarRekap.jsx
//
// Bendahara's Kas Keluar recap for an event — a read-only rollup of every
// Realisasi Nota entry (see RealisasiNota.jsx, which owns the actual
// create/edit/delete), grouped by section like Anggaran, plus a Rencana
// (RAB) vs Realisasi comparison and a button to generate the "1 file detail
// 1 event realisasi" Excel export that feeds LPJ.
//
// Entries themselves are still added/edited on the Realisasi Nota page —
// this page is the rollup + export, not another entry form.
import { Fragment, useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../../utils/config";
import { responseErrorMessage } from "../../utils/apiError";
import CustomSelect from "../../components/CustomSelect";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) throw new Error(await responseErrorMessage(res));
  return res.status === 204 ? null : res.json();
}

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

export default function KasKeluarRekap() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [items, setItems] = useState([]);
  const [rabTotal, setRabTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [exportedUrl, setExportedUrl] = useState("");

  useEffect(() => {
    apiFetch("/api/administrasi/events/?active_only=1")
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setEvents(list);
        if (list.length) setEventId(String(list[0].id));
      })
      .catch((e) => setError(e.message));
  }, []);

  const loadItems = useCallback(async () => {
    if (!eventId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/api/administrasi/kas-keluar/?event_id=${eventId}`);
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!eventId) {
      setRabTotal(0);
      return;
    }
    apiFetch(`/api/administrasi/rab-items/?event_id=${eventId}`)
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setRabTotal(list.reduce((sum, r) => sum + (r.total || 0), 0));
      })
      .catch(() => setRabTotal(0));
  }, [eventId]);

  const handleExport = async () => {
    setExportError("");
    setExportedUrl("");
    setExporting(true);
    try {
      const result = await apiFetch(`/api/administrasi/kas-keluar/export-excel/?event_id=${eventId}`, {
        method: "POST",
      });
      setExportedUrl(result.drive_file_url || "");
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const grouped = items.reduce((acc, it) => {
    const sectionKey = it.section || "Lain-lain";
    const kodeKey = it.kode || "Lain-lain";
    acc[sectionKey] = acc[sectionKey] || {};
    (acc[sectionKey][kodeKey] = acc[sectionKey][kodeKey] || []).push(it);
    return acc;
  }, {});
  const grandTotal = items.reduce((sum, it) => sum + it.total, 0);
  const selisih = rabTotal - grandTotal;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <h1 className="text-2xl font-semibold">Kas Keluar</h1>
        <button
          onClick={handleExport}
          disabled={!eventId || items.length === 0 || exporting}
          className="shrink-0 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
        >
          {exporting ? "Mengirim ke Drive..." : "Generate & Push ke Drive"}
        </button>
      </div>

      {exportedUrl && (
        <div className="glass-card p-3 mb-4 text-sm flex items-center justify-between gap-3">
          <span>Detail Realisasi berhasil dibuat & diupload ke Drive.</span>
          <a
            href={exportedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-brand-600 hover:underline font-medium"
          >
            Buka di Drive
          </a>
        </div>
      )}

      <div className="glass-card p-4 mb-4">
        <label className="block text-xs font-medium mb-1">Event</label>
        <CustomSelect
          value={eventId}
          onChange={setEventId}
          options={events.map((ev) => ({ value: String(ev.id), label: ev.name }))}
          placeholder="-- Pilih event --"
        />
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {exportError && <p className="text-sm text-red-600 mb-3">{exportError}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      {rabTotal > 0 && (
        <div className="glass-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted mb-0.5">Rencana (RAB)</div>
            <div className="font-semibold">{fmtRp(rabTotal)}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-0.5">Realisasi</div>
            <div className="font-semibold">{fmtRp(grandTotal)}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-0.5">Selisih</div>
            <div className={`font-semibold ${selisih < 0 ? "text-red-600" : "text-green-600"}`}>
              {fmtRp(Math.abs(selisih))} {selisih < 0 ? "(over budget)" : "(sisa)"}
            </div>
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([sectionName, kodeGroups]) => {
        const sectionSubtotal = Object.values(kodeGroups)
          .flat()
          .reduce((s, r) => s + r.total, 0);
        return (
          <div key={sectionName} className="glass-card p-4 mb-4">
            <h2 className="text-sm font-semibold mb-2">{sectionName}</h2>
            <div className="glass-table-wrap overflow-x-auto">
              <table className="glass-table text-xs">
                <thead>
                  <tr>
                    <th className="text-left">Tanggal</th>
                    <th className="text-left">Toko</th>
                    <th className="text-left">Uraian</th>
                    <th className="text-right">Qty</th>
                    <th className="text-left">Satuan</th>
                    <th className="text-right">Harga</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(kodeGroups).map(([kodeName, rows]) => {
                    const kodeSubtotal = rows.reduce((s, r) => s + r.total, 0);
                    return (
                      <Fragment key={kodeName}>
                        <tr className="bg-black/5 dark:bg-white/5">
                          <td
                            colSpan={7}
                            className="px-2 py-1 font-medium text-[11px] uppercase tracking-wide text-muted"
                          >
                            {kodeName}
                          </td>
                        </tr>
                        {rows.map((it) => (
                          <tr key={it.id} className="border-t">
                            <td className="px-2 py-1.5 whitespace-nowrap">{it.tanggal || "-"}</td>
                            <td className="px-2 py-1.5">{it.toko || "-"}</td>
                            <td className="px-2 py-1.5">{it.uraian}</td>
                            <td className="px-2 py-1.5 text-right">{it.qty}</td>
                            <td className="px-2 py-1.5">{it.satuan || "-"}</td>
                            <td className="px-2 py-1.5 text-right">{fmtRp(it.harga_satuan)}</td>
                            <td className="px-2 py-1.5 text-right font-medium">{fmtRp(it.total)}</td>
                          </tr>
                        ))}
                        <tr className="border-t">
                          <td colSpan={5} className="px-2 py-1 text-right text-muted">
                            Subtotal {kodeName}
                          </td>
                          <td colSpan={2} className="px-2 py-1 text-right text-muted">
                            {fmtRp(kodeSubtotal)}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                  <tr className="border-t font-semibold">
                    <td colSpan={5} className="px-2 py-1.5 text-right">
                      Subtotal {sectionName}
                    </td>
                    <td colSpan={2} className="px-2 py-1.5 text-right">
                      {fmtRp(sectionSubtotal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {!loading && items.length === 0 && (
        <p className="text-sm text-muted">
          Belum ada Realisasi Nota untuk event ini — tambahkan lewat halaman Realisasi Nota.
        </p>
      )}

      {items.length > 0 && (
        <div className="glass-card p-4 flex items-center justify-between">
          <span className="font-semibold">TOTAL REALISASI KESELURUHAN</span>
          <span className="font-semibold text-lg">{fmtRp(grandTotal)}</span>
        </div>
      )}
    </div>
  );
}
